import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DatePickerModule } from '@syncfusion/ej2-angular-calendars';
import { CheckBoxModule } from '@syncfusion/ej2-angular-buttons';
import { ChangeEventArgs as CheckBoxChangeEventArgs } from '@syncfusion/ej2-buttons';
import { AutoCompleteModule, DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { DialogModule, TooltipModule } from '@syncfusion/ej2-angular-popups';
import { DropDownButtonModule, ItemModel, MenuEventArgs } from '@syncfusion/ej2-angular-splitbuttons';
import { AppErrorMessagesComponent } from 'src/app/base/error-messages/app-error-messages.component';
import { ToastService } from 'src/app/base/services/toast.service';
import { ShowcaseOption, themeShowcaseClientOptions, themeShowcaseLocationOptions } from '../utils/theme-showcase-data';

interface ValidationDemoForm {
  bootstrapName: FormControl<string | null>;
  bootstrapEmail: FormControl<string | null>;
  syncfusionClient: FormControl<string | null>;
  syncfusionLocation: FormControl<string | null>;
  syncfusionDate: FormControl<Date | null>;
}

@Component({
  selector: 'app-syncfusion-showcase',
  templateUrl: './syncfusion-showcase.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    CheckBoxModule,
    DropDownListModule,
    AutoCompleteModule,
    DatePickerModule,
    TooltipModule,
    DropDownButtonModule,
    DialogModule,
    ReactiveFormsModule,
    AppErrorMessagesComponent
  ]
})
export class SyncfusionShowcaseComponent {
  public readonly dropdownFields = { text: 'label', value: 'value' };
  public readonly clientOptions: ShowcaseOption[] = themeShowcaseClientOptions;
  public readonly locationOptions: string[] = themeShowcaseLocationOptions;

  public splitButtonItems: ItemModel[] = [];
  public selectedDate = new Date();
  public syncfusionCheckboxChecked = true;
  public isDialogOpen = false;
  public latestAction = '';
  public validationAttempted = false;

  public validationDemoForm = this.formBuilder.group<ValidationDemoForm>({
    bootstrapName: this.formBuilder.control('', Validators.required),
    bootstrapEmail: this.formBuilder.control('', Validators.required),
    syncfusionClient: this.formBuilder.control<string | null>(null, Validators.required),
    syncfusionLocation: this.formBuilder.control('', Validators.required),
    syncfusionDate: this.formBuilder.control<Date | null>(null, Validators.required)
  });

  public get formFields() {
    return this.validationDemoForm.controls;
  }

  constructor(
    private readonly translate: TranslateService,
    private readonly formBuilder: FormBuilder,
    private readonly destroyRef: DestroyRef,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly toastService: ToastService
  ) {
    this.buildSplitButtonItems();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildSplitButtonItems());
  }

  public onCheckboxChanged(eventArgs: CheckBoxChangeEventArgs): void {
    this.syncfusionCheckboxChecked = Boolean(eventArgs.checked);
  }

  public onActionSelected(eventArgs: MenuEventArgs): void {
    this.latestAction = String(eventArgs.item.id || eventArgs.item.text || '');
  }

  public openDialog(): void {
    this.isDialogOpen = true;
  }

  public closeDialog(): void {
    this.isDialogOpen = false;
  }

  public showSuccessToast(): void {
    this.toastService.showSuccess(
      'i18n.domain.theme-showcase.toast.title',
      'i18n.domain.theme-showcase.toast.content'
    );
  }

  public showWarningToast(): void {
    this.toastService.showWarning(
      'i18n.domain.theme-showcase.toast.warningTitle',
      'i18n.domain.theme-showcase.toast.warningContent'
    );
  }

  public showErrorToast(): void {
    this.toastService.showError(
      'i18n.domain.theme-showcase.toast.errorTitle',
      'i18n.domain.theme-showcase.toast.errorContent'
    );
  }

  public triggerValidationDemo(): void {
    this.validationAttempted = true;
    this.validationDemoForm.markAllAsTouched();
    Object.values(this.validationDemoForm.controls).forEach((control) => {
      control.updateValueAndValidity({ onlySelf: true, emitEvent: true });
    });
    // Parent invokes this via @ViewChild, not through an @Input change or DOM event,
    // so OnPush would otherwise skip re-rendering the error-message bindings.
    this.changeDetector.markForCheck();
  }

  public resetValidationDemo(): void {
    this.validationAttempted = false;
    this.validationDemoForm.reset({
      bootstrapName: '',
      bootstrapEmail: '',
      syncfusionClient: null,
      syncfusionLocation: '',
      syncfusionDate: null
    });
    // Same reason as triggerValidationDemo: parent-driven mutation needs an explicit
    // dirty mark so [showUntouchedErrors] re-evaluates against the new state.
    this.changeDetector.markForCheck();
  }

  private buildSplitButtonItems(): void {
    this.splitButtonItems = [
      { id: 'sync', text: this.translate.instant('i18n.domain.theme-showcase.syncfusion.actionItems.sync') },
      { id: 'export', text: this.translate.instant('i18n.domain.theme-showcase.syncfusion.actionItems.export') },
      { id: 'archive', text: this.translate.instant('i18n.domain.theme-showcase.syncfusion.actionItems.archive') }
    ];
  }
}
